import { c as _c } from "react/compiler-runtime";
import { PlusIcon } from '@systemeio/icons';
import { BadRequest } from '@systemeio/util';
import { useEffect, useState, type JSX } from 'react';
import { Button, toast } from 'shared/components';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { CONTENT_HORIZONTAL_PADDING_CLASSNAME } from 'shared/layout/constants/layout-class-names';
import { SWRResponse } from 'swr';
import { twMerge } from 'tailwind-merge';
import { AutomationRuleTriggersEnum } from '../triggers/enums/trigger-enum';
import { AutomationRulesTriggersType } from '../triggers/types/automation-rule-trigger-interface';
import { AutomationRulesTriggersEditType } from '../triggers/types/automation-rule-triggers-edit-interface';
import { AutomationRuleApiInterface, RuleId } from '../types/automation-rule-interface';
import SpecificTriggerRuleCreate from './components/specific-trigger-rule-create';
import SpecificTriggerRuleEdit from './components/specific-trigger-rule-edit';
import SpecificTriggerRuleError from './components/specific-trigger-rule-error';
import SpecificTriggerRulesTable from './components/specific-trigger-rules-table';
export interface SpecificTriggerRulesProps {
  specificTriggerRulesData: SWRResponse<AutomationRuleApiInterface>;
  availableTriggersData: SWRResponse<AutomationRuleTriggersEnum[]>;
  noRelatedMessage: (ruleId: RuleId) => string | JSX.Element;
  getRelatedAndUnrelatedTriggersData: <T extends AutomationRulesTriggersEditType | AutomationRulesTriggersType>(triggers: Array<T>) => {
    relatedTriggers: T[];
    unrelatedTriggers: T[];
  };
  customAddTriggerHandler?: (trigger: AutomationRuleTriggersEnum, maxTriggerId: React.MutableRefObject<number>) => AutomationRulesTriggersEditType;
}
export default function SpecificTriggerRules(t0) {
  const $ = _c(25);
  const {
    availableTriggersData,
    getRelatedAndUnrelatedTriggersData,
    specificTriggerRulesData,
    customAddTriggerHandler,
    noRelatedMessage
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [commonError, setCommonError] = useState("");
  const [selectedRuleId, setSelectedRuleId] = useState();
  const [isCreating, setIsCreating] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setSelectedRuleId(undefined);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const clearSelectedRule = t1;
  let t2;
  let t3;
  if ($[1] !== specificTriggerRulesData.error) {
    t2 = () => {
      if (specificTriggerRulesData.error && specificTriggerRulesData.error instanceof BadRequest && specificTriggerRulesData.error.errors.common) {
        setCommonError(specificTriggerRulesData.error.errors.common.join(""));
      }
    };
    t3 = [specificTriggerRulesData.error];
    $[1] = specificTriggerRulesData.error;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== availableTriggersData.error) {
    t4 = () => {
      if (availableTriggersData.error && availableTriggersData.error instanceof BadRequest && availableTriggersData.error.errors.common) {
        toast.error(availableTriggersData.error.errors.common.join(""));
      }
    };
    t5 = [availableTriggersData.error];
    $[4] = availableTriggersData.error;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const hasRules = !!specificTriggerRulesData.data?.items.length;
  let t6;
  if ($[7] !== availableTriggersData.data || $[8] !== t) {
    t6 = className => <Button className={className} onClick={() => setIsCreating(true)} disabled={!availableTriggersData.data || availableTriggersData.data.length === 0} variant="primary" size="md"><PlusIcon />{t("dashboard.funnel.automation_rules.add_rule")}</Button>;
    $[7] = availableTriggersData.data;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const renderCreateButton = t6;
  let t7;
  if ($[10] !== commonError) {
    t7 = <SpecificTriggerRuleError error={commonError} />;
    $[10] = commonError;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== availableTriggersData || $[13] !== customAddTriggerHandler || $[14] !== getRelatedAndUnrelatedTriggersData || $[15] !== hasRules || $[16] !== isCreating || $[17] !== noRelatedMessage || $[18] !== renderCreateButton || $[19] !== selectedRuleId || $[20] !== specificTriggerRulesData) {
    t8 = selectedRuleId ? <SpecificTriggerRuleEdit noRelatedMessage={noRelatedMessage} mutateRules={specificTriggerRulesData.mutate} mutateAvailableTriggers={availableTriggersData.mutate} clearSelectedRule={clearSelectedRule} ruleId={selectedRuleId} availableTriggers={availableTriggersData.data} getRelatedAndUnrelatedTriggersData={getRelatedAndUnrelatedTriggersData} customAddTriggerHandler={customAddTriggerHandler} /> : isCreating ? <SpecificTriggerRuleCreate onClose={() => setIsCreating(false)} noRelatedMessage={noRelatedMessage} availableTriggersData={availableTriggersData} getRelatedAndUnrelatedTriggersData={getRelatedAndUnrelatedTriggersData} specificTriggerRulesData={specificTriggerRulesData} customAddTriggerHandler={customAddTriggerHandler} /> : <div className={twMerge(hasRules || specificTriggerRulesData.error ? "tablet:pb-0 pb-18" : undefined, hasRules && "tablet:max-desktop:-mx-2")}>{!specificTriggerRulesData.error && <SpecificTriggerRulesTable noRelatedMessage={noRelatedMessage} mutateAvailableTriggers={availableTriggersData.mutate} setSelectedRuleId={setSelectedRuleId} mutateRules={specificTriggerRulesData.mutate} rules={specificTriggerRulesData.data} getRelatedAndUnrelatedTriggersData={getRelatedAndUnrelatedTriggersData} emptyStateAction={renderCreateButton()} />}{(hasRules || !!specificTriggerRulesData.error) && <div className={twMerge(CONTENT_HORIZONTAL_PADDING_CLASSNAME, "bg-bg-primary border-border-100 tablet:static tablet:justify-end tablet:border-0 tablet:bg-transparent tablet:px-0 desktop:px-0 tablet:pt-4 tablet:pb-0 fixed inset-x-0 bottom-0 z-40 flex border-t py-4")}>{renderCreateButton("max-tablet:w-full")}</div>}</div>;
    $[12] = availableTriggersData;
    $[13] = customAddTriggerHandler;
    $[14] = getRelatedAndUnrelatedTriggersData;
    $[15] = hasRules;
    $[16] = isCreating;
    $[17] = noRelatedMessage;
    $[18] = renderCreateButton;
    $[19] = selectedRuleId;
    $[20] = specificTriggerRulesData;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t7 || $[23] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}